import React from "react"
import styled, { css } from "styled-components"
import colors from "@constants/colors"
import breakpoints from "@constants/breakpoints"
import {
  h1Styles,
  h2Styles,
  h3Styles,
  smallcapsBold14Styles,
} from "@constants/typography"
import TwoColumnBody from "./TwoColumnBody"
import ThreeColumnBody from "./ThreeColumnBody"
import { DesktopOnly } from "@utils/MediaQuery"

const TwoColumnContainerStyles = css`
  padding: 72px 0 99px;

  @media ${breakpoints.tablet} {
    display: flex;
    padding: 110px 0 143px;
    flex-wrap: wrap;
  }

  @media ${breakpoints.laptop} {
    display: flex;
    padding: 124px 0 209px;
    flex-wrap: wrap;
  }
`

const ThreeColumnContainerStyles = css`
  padding: 59px 20px 91px;

  @media ${breakpoints.tablet} {
    padding: 69px 0 148px calc(6 / 9 / 12 * 100vw);
  }

  @media ${breakpoints.laptop} {
    padding: 79px 0 182px calc(1 / 12 * 100vw);
  }
`

const Container = styled.div<{ layout: string }>`
  ${({ layout }) =>
    layout === "two column"
      ? TwoColumnContainerStyles
      : ThreeColumnContainerStyles}
`

const Decoration = styled.div`
  ${smallcapsBold14Styles}
`

const TopDecoration = styled(Decoration)`
  margin-bottom: 36px;
  padding-right: 10px;
  text-align: right;

  @media ${breakpoints.tablet} {
    margin-bottom: 48px;
    padding-left: calc((6 / 9 + 10) / 12 * 100vw);
    padding-right: 0;
    text-align: left;
  }

  @media ${breakpoints.laptop} {
    margin-bottom: 58px;
    padding-left: calc(10.5 / 12 * 100vw);
    text-align: left;
  }
`

const TopDecorationLeft = styled(TopDecoration)`
  @media ${breakpoints.laptop} {
    width: 100%;
    padding: calc(1 / 12 * 100vw * 1.5) 0 0 calc(0.5 / 12 * 100vw);
  }
`

const Headers = styled.div`
  margin-bottom: 38px;

  @media ${breakpoints.tablet} {
    display: flex;
    align-items: center;
  }

  @media ${breakpoints.laptop} {
    display: flex;
    margin-bottom: 120px;
    align-items: center;
  }
`

const Header = styled.h1<{ layout: string }>`
  ${({ layout }) => css`
    ${h3Styles}
    padding: 0 ${layout === "two column" ? "20px" : "0"};

    @media ${breakpoints.tablet} {
      ${h2Styles}
      padding-left: calc(3 / 9 / 12 * 100vw);
      padding-right: calc(3 / 12 * 100vw);
    }

    @media ${breakpoints.laptop} {
      ${h2Styles}
      width: calc(8 / 12 * 100vw - 20px);
      padding: 0;
      padding-right: calc(2 / 12 * 100vw);
    }

    @media ${breakpoints.desktop} {
      ${h1Styles}
    }
  `}
`

const Subheader = styled.div`
  width: calc(3 / 12 * 100vw);
  padding-right: calc(1.75 / 12 * 100vw);
  font-family: "Interstate Regular";
  font-size: 12px;
  color: ${colors.mediumGray};
  line-height: 24px;
  letter-spacing: 4px;
  text-transform: uppercase;
  opacity: 0.5;

  @media ${breakpoints.desktop} {
    letter-spacing: 8px;
  }
`

enum LayoutTypes {
  TWO_COLUMN = "two column",
  THREE_COLUMN = "three column",
}

interface HeroTwoUpProps {
  blockId?: string
  layout: string
  topDecoration?: Array<string>
  sideDecoration?: Array<string>
  header: string
  subheader?: string
  descriptionHeader?: string
  description: string
  leftImage: string
  leftImageCaption: string
  rightImage: string
  rightImageCaption: string
}

const HeroTwoUp: React.FC<HeroTwoUpProps> = ({
  blockId,
  layout,
  topDecoration,
  sideDecoration,
  header,
  subheader,
  descriptionHeader,
  description,
  leftImage,
  leftImageCaption,
  rightImage,
  rightImageCaption,
}) => (
  <Container layout={layout} id={blockId || undefined}>
    {topDecoration && layout === "two column" && (
      <TopDecorationLeft>{topDecoration.join(" ")}</TopDecorationLeft>
    )}
    {topDecoration && layout === "three column" && (
      <TopDecoration>{topDecoration.join(" ")}</TopDecoration>
    )}
    <Headers>
      <DesktopOnly>
        <Subheader>{subheader || ""}</Subheader>
      </DesktopOnly>
      <Header layout={layout}>{header}</Header>
    </Headers>
    {layout === LayoutTypes.TWO_COLUMN ? (
      <TwoColumnBody
        sideDecoration={sideDecoration}
        descriptionHeader={descriptionHeader}
        description={description}
        leftImage={leftImage}
        leftImageCaption={leftImageCaption}
        rightImage={rightImage}
        rightImageCaption={rightImageCaption}
      />
    ) : (
      <ThreeColumnBody
        sideDecoration={sideDecoration}
        descriptionHeader={descriptionHeader}
        description={description}
        leftImage={leftImage}
        leftImageCaption={leftImageCaption}
        rightImage={rightImage}
        rightImageCaption={rightImageCaption}
      />
    )}
  </Container>
)

export default HeroTwoUp
